import { useState, useEffect } from "react";
import { Plus, Loader2 } from "lucide-react";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ColorPicker } from "@/components/collections/ColorPicker";
import { toast } from "sonner";

interface CollectionModePanelProps {
  active: boolean;
  collectionName: string | null;
  collectionColor: string | null;
  count: number;
  onActivate: (col: { id: string; name: string; color: string }) => void;
  onDeactivate: () => void;
}

export function CollectionModePanel({
  active,
  collectionName,
  collectionColor,
  count,
  onActivate,
  onDeactivate,
}: CollectionModePanelProps) {
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState("");
  const [newColor, setNewColor] = useState("#3b82f6");
  // Local "selecting" state — true when switch is on but no collection picked yet
  const [selecting, setSelecting] = useState(false);

  const isOn = active || selecting;

  // Query collections only when the selector is visible
  const { data: collections, isLoading } = trpc.collections.list.useQuery(
    {},
    { enabled: selecting },
  );

  const utils = trpc.useUtils();
  const createMutation = trpc.collections.create.useMutation({
    onSuccess: (data) => {
      utils.collections.list.invalidate();
      onActivate({ id: data.id, name: data.name, color: data.color });
      setSelecting(false);
      setShowCreate(false);
      setNewName("");
    },
    onError: (err) => toast.error(err.message),
  });

  // When parent activates (collection picked), exit selecting state
  useEffect(() => {
    if (active) setSelecting(false);
  }, [active]);

  const handleSwitchChange = (checked: boolean) => {
    if (checked) {
      setSelecting(true);
    } else {
      setSelecting(false);
      setShowCreate(false);
      if (active) onDeactivate();
    }
  };

  const handleDeactivate = () => {
    setSelecting(false);
    setShowCreate(false);
    onDeactivate();
  };

  const handleSelect = (collectionId: string) => {
    const col = collections?.find((c: any) => c.id === collectionId);
    if (col) {
      onActivate({ id: col.id, name: col.name, color: col.color });
    }
  };

  const handleCreate = () => {
    if (!newName.trim()) return;
    createMutation.mutate({ name: newName.trim(), color: newColor });
  };

  return (
    <div className="rounded-lg border bg-card p-3 shadow-md space-y-2.5">
      <div className="flex items-center gap-2">
        <Switch
          id="collection-mode-toggle"
          checked={isOn}
          onCheckedChange={handleSwitchChange}
        />
        <Label htmlFor="collection-mode-toggle" className="text-sm cursor-pointer">
          Edit Collections
        </Label>
      </div>

      {selecting && (
        <>
          <Select onValueChange={handleSelect}>
            <SelectTrigger size="sm" className="w-full">
              <SelectValue placeholder="Select collection..." />
            </SelectTrigger>
            <SelectContent>
              {isLoading ? (
                <div className="flex justify-center py-2">
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                </div>
              ) : (collections ?? []).length === 0 ? (
                <div className="py-2 px-2 text-xs text-muted-foreground text-center">
                  No collections yet
                </div>
              ) : (
                (collections ?? []).map((col: any) => (
                  <SelectItem key={col.id} value={col.id}>
                    <span className="flex items-center gap-2">
                      <span
                        className="h-2.5 w-2.5 rounded-full shrink-0 inline-block"
                        style={{ backgroundColor: col.color }}
                      />
                      {col.name}
                    </span>
                  </SelectItem>
                ))
              )}
            </SelectContent>
          </Select>

          {!showCreate ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setShowCreate(true)}
              className="w-full justify-start text-muted-foreground"
            >
              <Plus className="h-4 w-4 mr-1.5" />
              Create New
            </Button>
          ) : (
            <div className="space-y-2 pt-1">
              <div className="space-y-1">
                <Label className="text-xs">Name</Label>
                <Input
                  placeholder="Collection name..."
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleCreate();
                  }}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Color</Label>
                <ColorPicker value={newColor} onChange={setNewColor} />
              </div>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setShowCreate(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  size="sm"
                  disabled={!newName.trim() || createMutation.isPending}
                  onClick={handleCreate}
                >
                  {createMutation.isPending && (
                    <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                  )}
                  Create
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {active && collectionName && (
        <>
          <div className="flex items-center gap-2">
            <span
              className="h-3 w-3 rounded-full shrink-0"
              style={{ backgroundColor: collectionColor ?? "#3b82f6" }}
            />
            <span className="text-sm font-medium truncate flex-1">
              {collectionName}
            </span>
            <span className="text-xs text-muted-foreground">
              {count}
            </span>
          </div>
          <Button size="sm" className="w-full" onClick={handleDeactivate}>
            Save
          </Button>
        </>
      )}
    </div>
  );
}
